import type { Identifier } from "ra-core";

import type { GulaClient } from "../gulaTypes";
import { inShift, slotsOf, type ShiftKey } from "../shifts";

/**
 * "Mi jornada": which leads to call first. Pure functions, the screen and the
 * database do the rest.
 */

/** Route of each user's own call list. */
export const MY_DAY_PATH = "/mi-jornada";

export type BucketKey =
  | "sure_close"
  | "recontact_due"
  | "hot_shift"
  | "closing"
  | "rescue"
  | "new_shift"
  | "retry"
  | "no_slot";

export interface Bucket {
  key: BucketKey;
  title: string;
  why: string;
  clients: GulaClient[];
}

/** A row of gula_call_missions_today. */
export interface CallMission {
  id: Identifier;
  sales_id: Identifier;
  client_id: Identifier;
  contact_id: Identifier;
  bucket: string;
  position: number;
  created_at?: string | null;
  done_at?: string | null;
  outcome?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  stage: string;
  lead_temperature?: string | null;
  call_slot?: string | null;
  call_attempts?: number | null;
  last_call_attempt_at?: string | null;
  recontact_at?: string | null;
  zone?: string | null;
  city?: string | null;
}

/** A row of gula_call_ranking_month: month totals plus today's calls. */
export interface RankingRow {
  id: Identifier;
  first_name: string;
  last_name?: string | null;
  /** Daily call goal */
  goal: number;
  /** Open calls in today's list */
  pending: number;
  today_calls: number;
  today_contacts: number;
  today_sales: number;
  today_points: number;
  /** Month totals */
  calls: number;
  contacts: number;
  sales: number;
  points: number;
}

/** First day of the month (YYYY-MM-01) in the team's time zone. */
export const monthStart = (date = new Date()): string =>
  `${madridDay(date).slice(0, 7)}-01`;

/** Moves a YYYY-MM-01 month by `delta` months. */
export const shiftMonth = (month: string, delta: number): string => {
  const [year, monthIndex] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, monthIndex - 1 + delta, 1));
  return date.toISOString().slice(0, 10);
};

/** "octubre" this year, "diciembre 2025" otherwise. */
export const monthLabel = (month: string, now = new Date()): string => {
  const name = new Date(`${month}T12:00:00Z`).toLocaleDateString("es-ES", {
    month: "long",
    timeZone: "UTC",
  });
  const year = month.slice(0, 4);
  return year === monthStart(now).slice(0, 4) ? name : `${name} ${year}`;
};

export const BUCKET_ORDER: BucketKey[] = [
  "sure_close",
  "recontact_due",
  "hot_shift",
  "closing",
  "rescue",
  "new_shift",
  "retry",
  "no_slot",
];

const BUCKET_TEXT: Record<BucketKey, { title: string; why: string }> = {
  sure_close: {
    title: "Compras seguras",
    why: "Ya han dicho que sí: falta cerrar el pago y registrar la venta",
  },
  recontact_due: {
    title: "Recontactos de hoy",
    why: "Pidieron que les llamáramos hoy: son los más fáciles de cerrar",
  },
  hot_shift: {
    title: "Calientes del turno",
    why: "Leads nuevos con muchas ganas que quieren llamada en esta franja",
  },
  closing: {
    title: "Para cerrar",
    why: "Ya hablamos con ellos y estaban interesados: toca el cierre",
  },
  rescue: {
    title: "Para rescatar",
    why: "Ya hablamos con ellos pero se quedaron parados: el primero que los trabaje se los queda",
  },
  new_shift: {
    title: "Nuevos del turno",
    why: "Primera llamada a los que eligieron esta franja",
  },
  retry: {
    title: "No contestan",
    why: "Otro intento a la hora que les va bien; los que ya probaste en su franja, en otra",
  },
  no_slot: {
    title: "Nuevos sin franja",
    why: "No dijeron horario: cualquier momento vale",
  },
};

const RETRY_GAP_MS = 3 * 60 * 60 * 1000;
/** No-answer leads tried this many times in their slot get a go in other slots. */
const OTHER_SLOT_AFTER = 2;

/** Leads taken in one go, and most untouched leads someone can hold at once. */
export const BATCH = 5;
export const OPEN_CAP = 10;
const TEMP_RANK: Record<string, number> = { hot: 0, warm: 1, cold: 2 };
const tempRank = (client: GulaClient) =>
  TEMP_RANK[client.lead_temperature ?? ""] ?? 3;

const byTempThenOldest = (a: GulaClient, b: GulaClient) =>
  tempRank(a) - tempRank(b) ||
  (a.stage_changed_at ?? "").localeCompare(b.stage_changed_at ?? "");

const madridDay = (date: Date) =>
  date.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });

const hasSlot = (client: GulaClient) =>
  !inShift({ stage: "new", call_slot: client.call_slot }, "none");

/** Call slot ("9-14", "14-18", "18-21") of a moment, in the team's time zone. */
export const slotAt = (date: Date): ShiftKey | null => {
  const hour = Number(
    date.toLocaleString("en-GB", {
      hour: "2-digit",
      hour12: false,
      timeZone: "Europe/Madrid",
    }),
  );
  if (hour >= 9 && hour < 14) return "9-14";
  if (hour >= 14 && hour < 18) return "14-18";
  if (hour >= 18 && hour < 21) return "18-21";
  return null;
};

/** Lead whose chosen slot is this shift (or any shift for "Todos"). */
const ownSlot = (client: GulaClient, shift: ShiftKey) =>
  shift === "all" || slotsOf(client.call_slot).includes(shift);

/**
 * Talks to follow up are called in the slot the lead chose; leads without a
 * slot fit every shift.
 */
const fitsShift = (client: GulaClient, shift: ShiftKey) =>
  shift === "all" ||
  (shift === "none"
    ? !hasSlot(client)
    : ownSlot(client, shift) || !hasSlot(client));

/**
 * A lead that did not answer is retried in its own slot; after a couple of
 * tries there, in another one. Never twice in the same slot on the same day.
 */
const retryFits = (client: GulaClient, shift: ShiftKey, now: Date) => {
  const last = client.last_call_attempt_at
    ? new Date(client.last_call_attempt_at)
    : null;
  if (last && now.getTime() - last.getTime() <= RETRY_GAP_MS) return false;
  if (shift === "all") return true;
  if (shift === "none") return !hasSlot(client);
  const lastSlot = last ? slotAt(last) : null;
  if (last && lastSlot === shift && madridDay(last) === madridDay(now))
    return false;
  return (
    !hasSlot(client) ||
    ownSlot(client, shift) ||
    ((client.call_attempts ?? 0) >= OTHER_SLOT_AFTER && lastSlot !== shift)
  );
};

/** Leads of their own slot first, then the rest. */
const ownSlotFirst =
  (shift: ShiftKey, then: (a: GulaClient, b: GulaClient) => number) =>
  (a: GulaClient, b: GulaClient) =>
    Number(!ownSlot(a, shift)) - Number(!ownSlot(b, shift)) || then(a, b);

/**
 * Buckets of leads still free to take (nobody's or mine, not in anyone's list
 * today), for the chosen shift, most valuable first. Empty buckets are left out.
 */
export const buildBuckets = ({
  clients,
  claimedClientIds,
  meId,
  shift,
  now = new Date(),
}: {
  clients: GulaClient[];
  claimedClientIds: Set<string>;
  meId?: Identifier | null;
  shift: ShiftKey;
  now?: Date;
}): Bucket[] => {
  const today = madridDay(now);
  const free = clients.filter(
    (client) =>
      !claimedClientIds.has(String(client.id)) &&
      (client.sales_id == null || String(client.sales_id) === String(meId)),
  );
  const inThisShift = (client: GulaClient) =>
    shift === "none"
      ? !hasSlot(client)
      : inShift({ stage: "new", call_slot: client.call_slot }, shift);

  const lists: Record<BucketKey, GulaClient[]> = {
    sure_close: free
      .filter((client) => client.stage === "sure_purchase")
      .sort(byTempThenOldest),
    recontact_due: free
      .filter(
        (client) =>
          client.stage === "recontact" &&
          !!client.recontact_at &&
          client.recontact_at.slice(0, 10) <= today,
      )
      .sort((a, b) =>
        (a.recontact_at ?? "").localeCompare(b.recontact_at ?? ""),
      ),
    hot_shift: free
      .filter(
        (client) =>
          client.stage === "new" &&
          client.lead_temperature === "hot" &&
          hasSlot(client) &&
          inThisShift(client),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    closing: free
      .filter(
        (client) =>
          ["first_contact", "second_contact"].includes(client.stage) &&
          ["hot", "warm"].includes(client.lead_temperature ?? "") &&
          fitsShift(client, shift),
      )
      .sort(ownSlotFirst(shift, byTempThenOldest)),
    rescue: free
      .filter(
        (client) =>
          client.sales_id == null &&
          client.released_from != null &&
          ["first_contact", "second_contact", "recontact"].includes(
            client.stage,
          ) &&
          !(
            ["first_contact", "second_contact"].includes(client.stage) &&
            ["hot", "warm"].includes(client.lead_temperature ?? "")
          ) &&
          !(
            client.stage === "recontact" &&
            !!client.recontact_at &&
            client.recontact_at.slice(0, 10) <= today
          ) &&
          fitsShift(client, shift),
      )
      .sort(ownSlotFirst(shift, byTempThenOldest)),
    new_shift: free
      .filter(
        (client) =>
          client.stage === "new" &&
          client.lead_temperature !== "hot" &&
          hasSlot(client) &&
          inThisShift(client),
      )
      .sort(byTempThenOldest),
    retry: free
      .filter(
        (client) =>
          client.stage === "no_answer" && retryFits(client, shift, now),
      )
      .sort(
        ownSlotFirst(
          shift,
          (a, b) =>
            (a.call_attempts ?? 0) - (b.call_attempts ?? 0) ||
            byTempThenOldest(a, b),
        ),
      ),
    no_slot: free
      .filter((client) => client.stage === "new" && !hasSlot(client))
      .sort(byTempThenOldest),
  };

  return BUCKET_ORDER.map((key) => ({
    key,
    ...BUCKET_TEXT[key],
    clients: lists[key],
  })).filter((bucket) => bucket.clients.length > 0);
};

/**
 * Leads in someone's list today still untouched: not done and no call, note,
 * WhatsApp or stage change since they took them. Only these count for the
 * cap, so whoever works fast can keep taking more.
 */
export const untouchedCalls = (
  missions: CallMission[],
  salesId: Identifier | null | undefined,
  lastActivityOf: (clientId: Identifier) => string | null | undefined,
): number =>
  missions.filter((mission) => {
    if (String(mission.sales_id) !== String(salesId) || mission.done_at)
      return false;
    const activity = lastActivityOf(mission.client_id);
    return (
      !activity ||
      !mission.created_at ||
      Date.parse(activity) < Date.parse(mission.created_at)
    );
  }).length;

/** How many more leads someone can take before working the ones they hold. */
export const roomLeft = (untouched: number): number =>
  Math.max(0, OPEN_CAP - untouched);

/**
 * The next `count` leads by priority across buckets, grouped by bucket so
 * each keeps its reason in the list.
 */
export const pickNext = (
  buckets: Bucket[],
  count: number,
): { bucket: BucketKey; ids: Identifier[] }[] => {
  const picks: { bucket: BucketKey; ids: Identifier[] }[] = [];
  let left = count;
  for (const bucket of buckets) {
    if (left <= 0) break;
    const ids = bucket.clients.slice(0, left).map((client) => client.id);
    if (ids.length) picks.push({ bucket: bucket.key, ids });
    left -= ids.length;
  }
  return picks;
};

export const bucketTitle = (key: string) =>
  BUCKET_TEXT[key as BucketKey]?.title ?? "Llamada";

export type CloseChance = "high" | "medium" | "low";

export const CLOSE_CHANCE_LABEL: Record<CloseChance, string> = {
  high: "Prob. alta",
  medium: "Prob. media",
  low: "Prob. baja",
};

/**
 * How likely a call is to end in a sale, from why it is in the list and how
 * keen the lead is. The list is already ordered this way; the label tells the
 * setter where to put the energy.
 */
export const closeChance = (
  bucket: string,
  temperature?: string | null,
): CloseChance => {
  if (["sure_close", "recontact_due", "hot_shift"].includes(bucket))
    return "high";
  if (bucket === "closing") return temperature === "hot" ? "high" : "medium";
  if (bucket === "retry") return "low";
  if (temperature === "hot") return "medium";
  if (temperature === "cold") return "low";
  return bucket === "rescue" || bucket === "new_shift" ? "medium" : "low";
};

/** The lead is happy to get a call in this slot (or did not choose one). */
export const isTheirTime = (
  callSlot: string | null | undefined,
  slot: string | null | undefined,
): boolean => {
  const slots = slotsOf(callSlot);
  return !slots.length || (!!slot && slots.includes(slot));
};

/**
 * Open missions first; within them, during a shift, the leads that want a
 * call now come first; then by bucket priority and the order taken.
 */
export const sortMissions = (missions: CallMission[], slot?: string | null) =>
  [...missions].sort(
    (a, b) =>
      Number(!!a.done_at) - Number(!!b.done_at) ||
      (slot
        ? Number(!isTheirTime(a.call_slot, slot)) -
          Number(!isTheirTime(b.call_slot, slot))
        : 0) ||
      BUCKET_ORDER.indexOf(a.bucket as BucketKey) -
        BUCKET_ORDER.indexOf(b.bucket as BucketKey) ||
      (TEMP_RANK[a.lead_temperature ?? ""] ?? 3) -
        (TEMP_RANK[b.lead_temperature ?? ""] ?? 3) ||
      a.position - b.position,
  );

/** Ranking order: points, then calls. Daily by default; the month is the accumulated view. */
export const sortRanking = (
  rows: RankingRow[],
  scope: "day" | "month" = "day",
) =>
  scope === "day"
    ? [...rows].sort(
        (a, b) =>
          b.today_points - a.today_points || b.today_calls - a.today_calls,
      )
    : [...rows].sort((a, b) => b.points - a.points || b.calls - a.calls);

export const POINTS_LEGEND =
  "Venta 10 · Mantenimiento 5 · Hablado 3 · Recontacto 3 · Bienvenida 3 · No compra 2 · No contesta 1";

export const OUTCOME_LABEL: Record<string, string> = {
  no_answer: "No contesta · +1",
  contacted: "Hablado · +3",
  recontact: "Recontactar · +3",
  lost: "No compra · +2",
  sale: "Venta · +10",
  skip: "Saltada",
};

export const missionName = (
  mission: Pick<CallMission, "first_name" | "last_name">,
) =>
  [mission.first_name, mission.last_name].filter(Boolean).join(" ") ||
  "Sin nombre";

/** Bucket of a lead taken by hand from the board ("Coger"). */
export const bucketForClient = (client: GulaClient): BucketKey => {
  if (client.stage === "sure_purchase") return "sure_close";
  if (client.stage === "recontact") return "recontact_due";
  if (client.stage === "no_answer") return "retry";
  if (["first_contact", "second_contact"].includes(client.stage))
    return "closing";
  if (!hasSlot(client)) return "no_slot";
  return client.lead_temperature === "hot" ? "hot_shift" : "new_shift";
};

/** Motivation line for the progress against the daily goal. */
export const progressStage = (calls: number, goal: number) => {
  const ratio = goal ? calls / goal : 0;
  if (ratio >= 1) return "Objetivo cumplido";
  if (ratio >= 0.75) return "Recta final";
  if (ratio >= 0.5) return "Más de la mitad";
  if (ratio >= 0.25) return "Cogiendo ritmo";
  return "Arrancando";
};

/** "Vas 1º" or how far the leader is. */
export const rankingLine = (
  rows: RankingRow[],
  meId?: Identifier | null,
): string => {
  const sorted = sortRanking(rows, "day");
  const index = sorted.findIndex((row) => String(row.id) === String(meId));
  if (index < 0 || !sorted[0].today_points) return "";
  if (index === 0) {
    const second = sorted[1];
    return second && sorted[0].today_points > 0
      ? `Vas 1º hoy, ${sorted[0].today_points - second.today_points} puntos por delante`
      : "Vas 1º hoy";
  }
  const leader = sorted[0];
  return `Vas ${index + 1}º hoy, a ${leader.today_points - sorted[index].today_points} puntos de ${leader.first_name}`;
};
